import addDays from 'date-fns/add_days';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import endOfMonth from 'date-fns/end_of_month';
import format from 'date-fns/format';
import isAfter from 'date-fns/is_after';
import isBefore from 'date-fns/is_before';
import isSameDay from 'date-fns/is_same_day';
import startOfDay from 'date-fns/start_of_day';
import startOfMonth from 'date-fns/start_of_month';
import startOfWeek from 'date-fns/start_of_week';
import { WsDay, WsDayType, WsEvent, WsMergedEvent, WsProdCalendarDay } from './work-schedule.models';
import { isSaturday, isSunday } from 'date-fns';

/** ISO (YYYY-MM-DD) начала дня */
export function iso(d: Date): string {
  return format(startOfDay(d), 'YYYY-MM-DD');
}

/** Лента дней для МЕСЯЦА: от понедельника перед 1-м числом до воскресенья после последнего */
export function buildMonthDays(base: Date, prodCalendarDays: WsProdCalendarDay[]): WsDay[] {
  const s = startOfMonth(base);
  const e = endOfMonth(base);
  const out: WsDay[] = [];
  let cur = s;
  while (!isAfter(cur, e)) {

    // let dayType = WsDayType.Workday;
    let dayType = WsDayType.Empty;

    if (prodCalendarDays) {
      let prodDay = prodCalendarDays.find(f => isSameDay(f.day, cur));
      if (prodDay && prodDay.dayType === WsDayType.Holiday) {
        dayType = prodDay.dayType;
      } else if (isSaturday(cur) || isSunday(cur)) {
        dayType = WsDayType.Weekend;
      }
    } else if (isSaturday(cur) || isSunday(cur)) {
      dayType = WsDayType.Weekend;
    }

    out.push({
      date: cur,
      type: dayType
    });

    cur = addDays(cur, 1);
  }

  return out;
}

export function diffInMonths(date1: Date, date2: Date): number {
  const years = date2.getFullYear() - date1.getFullYear();
  const months = date2.getMonth() - date1.getMonth();
  return years * 12 + months;
}

/** Лента дней для НЕДЕЛИ: понедельник..воскресенье недели base */
export function buildWeekDays(base: Date, prodCalendarDays: WsProdCalendarDay[]): WsDay[] {
  const s = startOfWeek(base);
  const out: WsDay[] = [];
  for (let i = 0; i < 7; i++) {
    let dayType = WsDayType.Workday;
    let cur = addDays(s, i + 1);

    if (prodCalendarDays) {
      let prodDay = prodCalendarDays.find(f => isSameDay(f.day, cur));
      if (prodDay) {
        dayType = prodDay.dayType;
      } else if (isSaturday(cur) || isSunday(cur)) {
        dayType = WsDayType.Weekend;
      }
    } else if (isSaturday(cur) || isSunday(cur)) {
      dayType = WsDayType.Weekend;
    }

    out.push({
      date: cur,
      type: dayType
    });
  }
  return out;
}

/** Сколько дней включает отрезок [start..end], включительно */
export function spanDays(start: Date, end: Date): number {
  return differenceInCalendarDays(end, start) + 1;
}

/** d находится в [s..e], границы включительно */
export function inRange(d: Date, s: Date, e: Date): boolean {
  return (isSameDay(d, s) || isAfter(d, s)) && (isSameDay(d, e) || isBefore(d, e));
}

/** Индекс дня в ленте (или -1) */
export function indexOfDay(days: Date[], target: Date): number {
  for (let i = 0; i < days.length; i++) if (isSameDay(days[i], target)) return i;
  return -1;
}

export function createEventId(id: number): string {
  return id.toString();
}

export function getEventId(rawId: string): number {
  return parseInt(rawId, 10);
}

export function getEventIds(rawIds: string[]): number[] {
  let res = [];

  rawIds.forEach(f => {
    res.push(parseInt(f, 10));
  });

  return res;
}

export function getDateRange(start: Date, end: Date): Date[] {
  const result: Date[] = [];
  const current = new Date(start);

  current.setHours(0, 0, 0, 0);

  while (current <= end) {
    result.push(new Date(current)); // важно: копия!
    current.setDate(current.getDate() + 1);
    current.setHours(0, 0, 0, 0);
  }

  return result;
}

export function isDatesIntersection(a: Date[], b: Date[]): boolean {
  return a.some(d1 => b.some(d2 => d1.getTime() === d2.getTime()));
}

/**
 * Возвращает один объединённый объект, если есть подряд идущие события.
 * Иначе возвращает переданный объект.
 * Если событий нет, возвращает null.
 */
export function getMergedSequentialEvent(events: WsEvent[], targetEventId: string): WsMergedEvent | null {
  if (!events.length) return null;

  // Сортируем по дате начала
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());

  const isNextDay = (d1: Date, d2: Date) =>
    d2.getTime() - d1.getTime() === 24 * 60 * 60 * 1000;

  const isPreviousDay = (d1: Date, d2: Date) =>
    d1.getTime() - d2.getTime() === 24 * 60 * 60 * 1000;

  let targetEvent = events.find(f => f.id === targetEventId);
  let currEvent = targetEvent;
  let targetEventIndex = sorted.indexOf(targetEvent);

  let mergedEvents: WsEvent[] = [];

  mergedEvents.push(targetEvent);

  if (targetEventIndex + 1 < sorted.length) {
    for (let i = targetEventIndex + 1; i < sorted.length; i++) {
      const ev = sorted[i];
      if (isNextDay(currEvent.end, ev.start)) {
        currEvent = ev;
        mergedEvents.push(ev);
      } else {
        break;
      }
    }
  }

  currEvent = targetEvent;

  if (targetEventIndex - 1 >= 0) {
    for (let i = targetEventIndex - 1; i >= 0; i--) {
      const ev = sorted[i];
      if (isPreviousDay(currEvent.start, ev.end)) {
        currEvent = ev;
        mergedEvents.unshift(ev);
      } else {
        break;
      }
    }
  }

  let currentStart = new Date(Math.min(...mergedEvents.map(e => e.start.getTime())));
  let currentEnd = new Date(Math.max(...mergedEvents.map(e => e.end.getTime())));

  return {
      ids: mergedEvents.map(e => e.id),
      start: currentStart,
      end: currentEnd,
      employeeId: sorted[0].employeeId,
      type: sorted[0].type,
      icon: sorted[0].icon,
    };
}

export function getPopupPosition(et: EventTarget, popupWidth: number, popupHeight: number, margin: number): { top: number, left: number } {
  const target = et as HTMLElement;
  const rect = target.getBoundingClientRect();

  // начальные координаты
  let left = rect.right + margin;
  let top = rect.top;

  // если справа не помещается — сдвигаем влево
  if (left + popupWidth + margin > window.innerWidth) {
    left = rect.left - popupWidth - margin;
  }

  // если снизу не помещается — поднимаем вверх
  if (top + popupHeight + margin > window.innerHeight) {
    top = rect.bottom - popupHeight - margin;
  }

  // защита от выхода за пределы сверху и слева
  if (top < 0) top = margin;
  if (left < 0) left = margin;

  return {
    top,
    left
  };
}